/**
 * The Cloud Storage resumable-upload exchange, for the two places this frontend
 * puts bytes into a bucket without them passing through the API: a consult
 * recording (ADR-018) and an EPD import archive (#3771).
 *
 * Both are the same protocol against a different initiate endpoint, and the
 * protocol is the part that is easy to get subtly wrong — a `Content-Range` off
 * by one, a `308` read as a failure, a resume that restarts from zero. It is
 * written once here so a second upload path inherits it rather than re-deriving
 * it.
 *
 * Where the two differ is only *who opens the session*. Whisper's initiate
 * answers with a session URI it opened server-side, so a recording starts at
 * {@link uploadFromOffset}; the import's initiate answers with a signed URL that
 * only authorises **opening** one, so an archive starts at
 * {@link startResumableSession}. That is why the two steps are separate
 * functions rather than one call.
 *
 * `XMLHttpRequest` rather than `fetch`, deliberately: upload progress over a
 * multi-gigabyte body is what `xhr.upload.onprogress` reports and what `fetch`
 * still cannot. A spinner with no percentage is not an answer for an archive
 * that takes minutes.
 *
 * Nothing here is user-facing. Every rejection is a {@link ResumableUploadError}
 * naming *what kind* of failure it was, and the composable above decides what to
 * say about it — the copy belongs to the screen, and a transport module has no
 * business holding a translation.
 */

/**
 * Why an upload against a resumable session did not finish.
 *
 * - `network` — the request never got an answer (offline, DNS, TLS, CORS).
 * - `aborted` — something called `abort()` on it; a cancellation, not a failure.
 * - `status` — the server answered, and the answer was not one that means
 *   progress. Carries the code.
 */
export type ResumableFailure = 'network' | 'aborted' | 'status'

/**
 * A failure of the transfer itself.
 *
 * Its `message` is developer English on purpose and never reaches a user:
 * `extractApiErrorMessage` ignores the `message` of anything that is not a
 * `UserFacingError` (see `utils/errorHandling`), so a caller has to map
 * {@link failure} onto its own copy.
 */
export class ResumableUploadError extends Error {
  constructor(
    readonly failure: ResumableFailure,
    readonly status?: number
  ) {
    super(`Resumable upload ${failure}${status === undefined ? '' : ` (HTTP ${status})`}`)
    this.name = 'ResumableUploadError'
  }
}

/** How far a transfer has got, in the form a progress bar reads. */
export interface UploadProgress {
  bytesUploaded: number
  totalBytes: number
  /** 0-100, rounded. An upload of nothing is 0 rather than a division by zero. */
  percentage: number
}

/**
 * The one rule for "how full is this upload", shared by both callers so a
 * recording and an archive cannot round their bar differently.
 */
export function toUploadProgress(bytesUploaded: number, totalBytes: number): UploadProgress {
  return {
    bytesUploaded,
    totalBytes,
    percentage: totalBytes > 0 ? Math.round((bytesUploaded / totalBytes) * 100) : 0,
  }
}

/** The header that turns a signed `POST` into the opening of a resumable session. */
const RESUMABLE_HEADER = 'x-goog-resumable'
const RESUMABLE_HEADER_VALUE = 'start'

/** Cloud Storage's "I have part of it, keep going" answer to a range request. */
const HTTP_RESUME_INCOMPLETE = 308

function isSuccess(status: number): boolean {
  return status === 200 || status === 201
}

/**
 * Open a resumable session against a signed URL, and answer with the session URI
 * the bytes then go to.
 *
 * The `x-goog-resumable: start` header is **part of what the URL was signed
 * for** (`GcsImportUploadStorage`): a URL signed with it is refused without it,
 * and the body must stay empty. `send()` with no argument is what keeps it that
 * way — passing anything would also make the browser set a `Content-Type` that
 * was deliberately left out of the signature.
 *
 * A `200`/`201` with no readable `Location` is treated as a failure rather than
 * as an empty session URI, because it has exactly one cause worth naming: the
 * bucket's CORS `responseHeader` list does not expose `Location`, so the header
 * arrived and JavaScript cannot see it (`terraform/modules/scrivio/main.tf`,
 * `google_storage_bucket.import_uploads`).
 */
export function startResumableSession(signedUrl: string): Promise<string> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open('POST', signedUrl, true)
    xhr.setRequestHeader(RESUMABLE_HEADER, RESUMABLE_HEADER_VALUE)

    xhr.onload = () => {
      if (!isSuccess(xhr.status)) {
        reject(new ResumableUploadError('status', xhr.status))
        return
      }
      const sessionUri = xhr.getResponseHeader('Location')
      if (!sessionUri) {
        reject(new ResumableUploadError('status', xhr.status))
        return
      }
      resolve(sessionUri)
    }
    xhr.onerror = () => reject(new ResumableUploadError('network'))
    xhr.onabort = () => reject(new ResumableUploadError('aborted'))

    xhr.send()
  })
}

/**
 * Ask an open session how many bytes it already holds, so a resume continues
 * from there instead of from zero.
 *
 * The question is a bodyless `PUT` whose `Content-Range` names a wildcard over
 * the total size. Cloud Storage answers `308` with a `Range` header for a
 * partial upload, `200`/`201` for one that is in fact already complete, and the
 * caller sees a byte count either way. A `308` with **no** `Range` means the
 * session has nothing yet, which is `0` rather than an error.
 *
 * No `Content-Length: 0` is set: it is a forbidden header name, so the browser
 * ignores it and warns, and a body-less `send()` already produces it.
 */
export function queryUploadedBytes(sessionUri: string, totalBytes: number): Promise<number> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open('PUT', sessionUri, true)
    xhr.setRequestHeader('Content-Range', `bytes */${totalBytes}`)

    xhr.onload = () => {
      if (xhr.status === HTTP_RESUME_INCOMPLETE) {
        const range = xhr.getResponseHeader('Range')
        const match = range?.match(/bytes=0-(\d+)/)
        resolve(match?.[1] ? parseInt(match[1], 10) + 1 : 0)
        return
      }
      if (isSuccess(xhr.status)) {
        resolve(totalBytes)
        return
      }
      reject(new ResumableUploadError('status', xhr.status))
    }
    xhr.onerror = () => reject(new ResumableUploadError('network'))
    xhr.onabort = () => reject(new ResumableUploadError('aborted'))

    xhr.send()
  })
}

export interface UploadFromOffsetOptions {
  /** The session URI, from {@link startResumableSession} or an initiate endpoint. */
  sessionUri: string
  /** The whole object. Only the part from `startByte` on is sent. */
  body: Blob
  /** Where to resume from; `0` for a fresh transfer. */
  startByte: number
  /** Called as the bytes go out, with the total already sent. */
  onProgress?: (bytesUploaded: number, totalBytes: number) => void
  /**
   * Handed the request as soon as it exists, so a caller that offers a cancel
   * button has something to `abort()`. The abort surfaces as a
   * {@link ResumableUploadError} with failure `aborted`.
   */
  onRequestCreated?: (xhr: XMLHttpRequest) => void
}

/**
 * Send the remainder of `body` to an open session.
 *
 * A `308` resolves rather than rejecting: it says the session took what it was
 * given and wants the rest, which is progress. The caller resumes by asking
 * {@link queryUploadedBytes} again — that loop belongs to the caller, because
 * only it knows how many attempts are worth making.
 *
 * Nothing left to send resolves immediately. `bytes N-(N-1)/N` is not a range
 * Cloud Storage accepts, and "the session already has all of it" is precisely
 * the answer {@link queryUploadedBytes} gives a caller that is resuming a
 * transfer which in fact finished.
 */
export function uploadFromOffset(options: UploadFromOffsetOptions): Promise<void> {
  const { sessionUri, body, startByte, onProgress, onRequestCreated } = options
  const totalBytes = body.size

  return new Promise((resolve, reject) => {
    if (startByte >= totalBytes) {
      onProgress?.(totalBytes, totalBytes)
      resolve()
      return
    }

    const xhr = new XMLHttpRequest()
    onRequestCreated?.(xhr)
    xhr.open('PUT', sessionUri, true)
    xhr.setRequestHeader('Content-Range', `bytes ${startByte}-${totalBytes - 1}/${totalBytes}`)

    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) onProgress?.(startByte + event.loaded, totalBytes)
    }

    xhr.onload = () => {
      if (isSuccess(xhr.status)) {
        onProgress?.(totalBytes, totalBytes)
        resolve()
        return
      }
      if (xhr.status === HTTP_RESUME_INCOMPLETE) {
        resolve()
        return
      }
      reject(new ResumableUploadError('status', xhr.status))
    }
    xhr.onerror = () => reject(new ResumableUploadError('network'))
    xhr.onabort = () => reject(new ResumableUploadError('aborted'))

    xhr.send(body.slice(startByte))
  })
}
